/**
 * Kartica »Register osnovnih sredstev« na strani Uvoz (docs/ARHITEKTURA.md,
 * 15.2): mesecno nalaganje izvoza registra, seznam veljavnih registrov z
 * vsotami, razclenitev po sredstvih in amortizacijske skupine podjetja.
 * Samo skrbnik, samo pri podjetju z vklopljenim modulom Projekti.
 */
import type { AmortizacijskaSkupina, IzbiraZavihka, IzidUvozaRegistra, PregledRegistrov, SredstvoRegistra } from '@koda/shared';
import { useEffect, useState, type FormEvent, type JSX } from 'react';

import { api } from '../lib/api';
import { datum, datumInUra, decimalno, znesek } from '../lib/format';
import { OpozorilaUvoza } from './OpozorilaUvoza';

const MESECI = ['januar', 'februar', 'marec', 'april', 'maj', 'junij', 'julij', 'avgust', 'september', 'oktober', 'november', 'december'];

interface Lastnosti {
  podjetje: string;
}

/** Stopnja v odstotkih brez odvecnih nicel: 33,33 %, 50 %, 6,25 %. */
export function stopnjaZaPrikaz(stopnja: string | null): string {
  if (stopnja === null) return '—';
  const stevilo = Number(stopnja);
  if (!Number.isFinite(stevilo)) return stopnja;
  const decimalk = Number.isInteger(stevilo) ? 0 : Number.isInteger(stevilo * 100) ? 2 : 3;
  return `${decimalno(String(stevilo), decimalk)} %`;
}

/** Stopnja iz slovenskega zapisa (33,33) v decimalno besedilo za API (33.33). */
export function stopnjaZaApi(besedilo: string): string {
  const ociscen = besedilo.replace(/[\s%]/g, '').replace(',', '.');
  if (!/^\d+(\.\d{1,3})?$/.test(ociscen) || Number(ociscen) > 100) {
    throw new Error('Stopnja mora biti odstotek med 0 in 100, npr. 33,33.');
  }
  return ociscen;
}

/** Privzeti mesec nalaganja: pretekli mesec, ker se register izvozi po koncu meseca. */
function privzetoObdobje(zdaj = new Date()): { leto: number; mesec: number } {
  const mesec = zdaj.getMonth(); // 0 = januar -> pretekli mesec je getMonth()
  return mesec === 0 ? { leto: zdaj.getFullYear() - 1, mesec: 12 } : { leto: zdaj.getFullYear(), mesec };
}

export function RegisterOs({ podjetje }: Lastnosti): JSX.Element {
  const [pregled, setPregled] = useState<PregledRegistrov | null>(null);
  const [napaka, setNapaka] = useState<string | null>(null);

  const zacetek = privzetoObdobje();
  const [leto, setLeto] = useState(String(zacetek.leto));
  const [mesec, setMesec] = useState(String(zacetek.mesec));
  const [datoteka, setDatoteka] = useState<File | null>(null);
  const [tece, setTece] = useState(false);
  const [izid, setIzid] = useState<IzidUvozaRegistra | null>(null);
  const [zavihki, setZavihki] = useState<IzbiraZavihka | null>(null);

  const [odprt, setOdprt] = useState<{ uvoz: string; sredstva: SredstvoRegistra[] } | null>(null);
  const [skupine, setSkupine] = useState<{ koda: string; stopnja: string }[] | null>(null);
  const [napakaSkupin, setNapakaSkupin] = useState<string | null>(null);

  const osvezi = (): Promise<void> =>
    api
      .registri(podjetje)
      .then((p) => {
        setPregled(p);
        setNapaka(null);
      })
      .catch((e: unknown) => {
        setNapaka(e instanceof Error ? e.message : 'Registrov ni bilo mogoče prebrati.');
      });

  useEffect(() => {
    let aktivno = true;
    api
      .registri(podjetje)
      .then((p) => {
        if (aktivno) setPregled(p);
      })
      .catch((e: unknown) => {
        if (aktivno) setNapaka(e instanceof Error ? e.message : 'Registrov ni bilo mogoče prebrati.');
      });
    return () => {
      aktivno = false;
    };
  }, [podjetje]);

  const nalozi = (zavihek?: string): void => {
    if (!datoteka) return;
    setTece(true);
    setIzid(null);
    setZavihki(null);
    setNapaka(null);
    api
      .naloziRegister(podjetje, Number.parseInt(leto, 10), Number.parseInt(mesec, 10), datoteka, zavihek)
      .then((odgovor) => {
        if ('zavihki' in odgovor) {
          setZavihki(odgovor);
          return;
        }
        setIzid(odgovor);
        if (!odgovor.potrjen) return undefined;
        // Nov register je lahko nadomestil odprtega: razclenitev bi kazala
        // stara sredstva pod novimi vsotami, zato se zapre.
        setOdprt(null);
        return osvezi();
      })
      .catch((e: unknown) => {
        setNapaka(e instanceof Error ? e.message : 'Registra ni bilo mogoče naložiti.');
      })
      .finally(() => {
        setTece(false);
      });
  };

  const poslji = (dogodek: FormEvent): void => {
    dogodek.preventDefault();
    nalozi();
  };

  const odpri = (uvoz: string): void => {
    if (odprt?.uvoz === uvoz) {
      setOdprt(null);
      return;
    }
    api
      .sredstvaRegistra(podjetje, uvoz)
      .then((sredstva) => {
        setOdprt({ uvoz, sredstva });
      })
      .catch((e: unknown) => {
        setNapaka(e instanceof Error ? e.message : 'Sredstev ni bilo mogoče prebrati.');
      });
  };

  const umakni = (uvoz: string, opis: string): void => {
    if (!window.confirm(`Umaknem register ${opis}? Sredstva tega meseca bodo izbrisana, izvirnik ostane v arhivu.`)) return;
    api
      .umakniRegister(podjetje, uvoz)
      .then(() => {
        if (odprt?.uvoz === uvoz) setOdprt(null);
        return osvezi();
      })
      .catch((e: unknown) => {
        setNapaka(e instanceof Error ? e.message : 'Registra ni bilo mogoče umakniti.');
      });
  };

  const shraniSkupine = (dogodek: FormEvent): void => {
    dogodek.preventDefault();
    if (skupine === null) return;
    let vnos: AmortizacijskaSkupina[];
    try {
      vnos = skupine
        .filter((s) => s.koda.trim() !== '' || s.stopnja.trim() !== '')
        .map((s) => ({ koda: s.koda.trim(), stopnja: stopnjaZaApi(s.stopnja) }));
    } catch (e) {
      setNapakaSkupin(e instanceof Error ? e.message : 'Neveljaven vnos.');
      return;
    }
    api
      .shraniAmortizacijskeSkupine(podjetje, vnos)
      .then((shranjene) => {
        setSkupine(null);
        setNapakaSkupin(null);
        setPregled((p) => (p === null ? p : { ...p, skupine: shranjene }));
      })
      .catch((e: unknown) => {
        setNapakaSkupin(e instanceof Error ? e.message : 'Skupin ni bilo mogoče shraniti.');
      });
  };

  const opisRegistra = (r: { leto: number; mesec: number }): string => `${MESECI[r.mesec - 1] ?? String(r.mesec)} ${String(r.leto)}`;

  return (
    <section className="kartica" aria-labelledby="register-os">
      <div className="glava-kartice">
        <h2 id="register-os">Register osnovnih sredstev</h2>
      </div>
      <p className="podnaslov">
        Vsak mesec naloži izvoz registra iz programa (Excel). Stolpci se prepoznajo po imenu; uvozijo se samo
        aktivna sredstva. Stopnja amortizacije se vzame iz amortizacijske skupine podjetja (spodaj), ker jo izvoz
        zaokroži; kadar skupine ni, velja stopnja iz izvoza. Nov register istega meseca nadomesti prejšnjega.
      </p>

      <form onSubmit={poslji} className="obrazec">
        <label>
          Leto
          <input
            type="number"
            value={leto}
            min={1990}
            max={2999}
            onChange={(d) => {
              setLeto(d.target.value);
            }}
            required
          />
        </label>
        <label>
          Mesec
          <select
            value={mesec}
            onChange={(d) => {
              setMesec(d.target.value);
            }}
          >
            {MESECI.map((ime, i) => (
              <option key={ime} value={i + 1}>
                {ime}
              </option>
            ))}
          </select>
        </label>
        <label>
          Izvoz registra (.xlsx)
          <input
            type="file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={(d) => {
              setDatoteka(d.target.files?.[0] ?? null);
              setIzid(null);
              setZavihki(null);
            }}
          />
        </label>
        <button type="submit" disabled={tece || !datoteka}>
          {tece ? 'Nalagam …' : 'Naloži register'}
        </button>
      </form>

      {zavihki !== null && (
        <div className="opozorilo">
          <p>{zavihki.sporocilo}</p>
          <p>
            {zavihki.zavihki.map((z) => (
              <button
                key={z}
                type="button"
                className="gumb-besedilo"
                style={{ marginRight: '0.6rem' }}
                onClick={() => {
                  nalozi(z);
                }}
              >
                {z}
              </button>
            ))}
          </p>
        </div>
      )}

      {izid !== null && (
        <div className={izid.potrjen ? 'uspeh' : 'napaka'} role="status">
          <p>
            {izid.potrjen
              ? `Register je uvožen: ${String(izid.sredstev)} aktivnih sredstev` +
                (izid.preskocenih > 0 ? `, ${String(izid.preskocenih)} neaktivnih vrstic preskočenih.` : '.')
              : 'Register ni uvožen. Odpravi napake in ga naloži znova.'}
          </p>
          <OpozorilaUvoza opozorila={izid.opozorila} />
        </div>
      )}

      {napaka !== null && (
        <p className="napaka" role="alert">
          {napaka}
        </p>
      )}

      {pregled !== null && pregled.registri.length === 0 && <p className="drobno">Podjetje še nima naloženega registra.</p>}
      {pregled !== null && pregled.registri.length > 0 && (
        <div className="tabela-ovoj">
          <table>
            <thead>
              <tr>
                <th>register</th>
                <th className="stevilka">sredstev</th>
                <th className="stevilka">nabavna vrednost</th>
                <th className="stevilka">popravek vrednosti</th>
                <th className="stevilka">neodpisana vrednost</th>
                <th className="stevilka">mesečna amortizacija</th>
                <th>naloženo</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {pregled.registri.map((r) => (
                <tr key={r.uvoz}>
                  <td>
                    <button
                      type="button"
                      className="gumb-besedilo"
                      aria-expanded={odprt?.uvoz === r.uvoz}
                      onClick={() => {
                        odpri(r.uvoz);
                      }}
                    >
                      {opisRegistra(r)}
                    </button>
                    {r.opozorila.some((o) => !o.napaka) && <span className="drobno"> · z opozorili</span>}
                  </td>
                  <td className="stevilka">{r.sredstev}</td>
                  <td className="stevilka">{znesek(r.nabavnaVrednost)}</td>
                  <td className="stevilka">{znesek(r.popravekVrednosti)}</td>
                  <td className="stevilka">{znesek(r.neodpisanaVrednost)}</td>
                  <td className="stevilka">{r.mesecnaAmortizacija === null ? '—' : znesek(r.mesecnaAmortizacija)}</td>
                  <td>
                    {datumInUra(r.nalozeno)}
                    {r.datoteka !== null && <span className="drobno"> ({r.datoteka})</span>}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="gumb-besedilo"
                      onClick={() => {
                        umakni(r.uvoz, opisRegistra(r));
                      }}
                    >
                      umakni
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {odprt !== null && (
        <div className="tabela-ovoj" style={{ marginTop: '0.75rem' }}>
          <table>
            <caption className="drobno">Sredstva registra ({String(odprt.sredstva.length)})</caption>
            <thead>
              <tr>
                <th>inv. št.</th>
                <th>naziv</th>
                <th>aktivirano</th>
                <th>skupina</th>
                <th className="stevilka">stopnja</th>
                <th className="stevilka">nabavna vrednost</th>
                <th className="stevilka">popravek vrednosti</th>
                <th className="stevilka">neodpisana vrednost</th>
                <th className="stevilka">mesečna amortizacija</th>
                <th>str. nosilec</th>
              </tr>
            </thead>
            <tbody>
              {odprt.sredstva.map((s) => (
                <tr key={s.id}>
                  <td>
                    <code>{s.inventarna}</code>
                  </td>
                  <td>{s.naziv}</td>
                  <td>{s.aktivirano === null ? '—' : datum(s.aktivirano)}</td>
                  <td>{s.skupina ?? '—'}</td>
                  <td className="stevilka" title={s.virStopnje === 'izvoz' ? 'stopnja iz izvoza (skupina ni vnesena)' : s.virStopnje === 'skupina' ? 'stopnja iz amortizacijske skupine' : undefined}>
                    {stopnjaZaPrikaz(s.stopnja)}
                    {s.virStopnje === 'izvoz' && <span className="drobno"> (izvoz)</span>}
                  </td>
                  <td className="stevilka">{znesek(s.nabavnaVrednost)}</td>
                  <td className="stevilka">{znesek(s.popravekVrednosti)}</td>
                  <td className="stevilka">{znesek(s.neodpisanaVrednost)}</td>
                  <td className="stevilka">{s.mesecnaAmortizacija === null ? '—' : znesek(s.mesecnaAmortizacija)}</td>
                  <td>{s.nosilec ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h3 style={{ marginTop: '1.25rem' }}>Amortizacijske skupine</h3>
      <p className="drobno">
        Šifra skupine, kot je v programu, in letna stopnja v odstotkih (npr. 33K → 33,33). Po spremembi register
        naloži znova, da se stopnje osvežijo.
      </p>
      {skupine === null ? (
        <>
          {pregled !== null && pregled.skupine.length > 0 && (
            <p>
              {pregled.skupine.map((s) => (
                <span key={s.koda} style={{ marginRight: '1rem' }}>
                  <code>{s.koda}</code> {stopnjaZaPrikaz(s.stopnja)}
                </span>
              ))}
            </p>
          )}
          {pregled !== null && pregled.skupine.length === 0 && <p className="drobno">Skupine še niso vnesene.</p>}
          <button
            type="button"
            className="gumb-besedilo"
            onClick={() => {
              setSkupine([...(pregled?.skupine ?? []).map((s) => ({ koda: s.koda, stopnja: stopnjaZaPrikaz(s.stopnja).replace(' %', '') })), { koda: '', stopnja: '' }]);
              setNapakaSkupin(null);
            }}
          >
            uredi skupine
          </button>
        </>
      ) : (
        <form className="obrazec" onSubmit={shraniSkupine}>
          {skupine.map((s, i) => (
            <div key={String(i)} className="vrstica-obrazca" style={{ display: 'flex', gap: '0.5rem' }}>
              <label>
                Šifra skupine
                <input
                  type="text"
                  value={s.koda}
                  onChange={(d) => {
                    setSkupine(skupine.map((x, j) => (j === i ? { ...x, koda: d.target.value } : x)));
                  }}
                />
              </label>
              <label>
                Stopnja (%)
                <input
                  type="text"
                  inputMode="decimal"
                  value={s.stopnja}
                  onChange={(d) => {
                    setSkupine(skupine.map((x, j) => (j === i ? { ...x, stopnja: d.target.value } : x)));
                  }}
                />
              </label>
            </div>
          ))}
          <button
            type="button"
            className="gumb-besedilo"
            onClick={() => {
              setSkupine([...skupine, { koda: '', stopnja: '' }]);
            }}
          >
            dodaj vrstico
          </button>
          {napakaSkupin !== null && (
            <p className="napaka" role="alert">
              {napakaSkupin}
            </p>
          )}
          <div>
            <button type="submit">Shrani skupine</button>{' '}
            <button
              type="button"
              className="gumb-besedilo"
              onClick={() => {
                setSkupine(null);
                setNapakaSkupin(null);
              }}
            >
              prekliči
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
